class TestimonialCarouselLive extends HTMLElement {
  connectedCallback() {
    this.slidesWrap = this.querySelector('.testimonial-carousel__slides');
    this.dotsWrap = this.querySelector('.testimonial-carousel__dots');
    this.prev = this.querySelector('.testimonial-carousel__arrow--prev');
    this.next = this.querySelector('.testimonial-carousel__arrow--next');
    this.index = 0;

    if (this.prev) this.prev.addEventListener('click', () => this.go(this.index - 1));
    if (this.next) this.next.addEventListener('click', () => this.go(this.index + 1));

    this.collect();
    this.load();
  }

  collect() {
    this.slides = Array.from(this.querySelectorAll('.testimonial-carousel__slide'));
    this.dots = Array.from(this.querySelectorAll('.testimonial-carousel__dot'));
    this.dots.forEach((dot) => dot.addEventListener('click', () => this.go(Number(dot.dataset.index))));

    const multiple = this.slides.length > 1;
    [this.prev, this.next, this.dotsWrap].forEach((el) => {
      if (el) el.hidden = !multiple;
    });
  }

  async load() {
    const { store, tag, reviewType, limit } = this.dataset;
    if (!store || !tag) return;

    const url =
      'https://api.reviews.io/timeline/data?' +
      new URLSearchParams({
        store,
        tag,
        type: reviewType || 'product_review',
        sort: 'date_desc',
        page: '1',
        per_page: limit || '6',
        lang: 'en',
      });

    let reviews = [];
    try {
      const data = await fetch(url).then((r) => (r.ok ? r.json() : null));
      reviews = ((data && data.timeline) || []).map((item) => item._source).filter((r) => r && r.comments);
    } catch (e) {
      reviews = [];
    }

    this.renderCount();

    if (!reviews.length) {
      this.revealStars();
      return;
    }

    this.render(reviews);
    this.collect();
    this.go(0);
    this.revealStars();
  }

  revealStars() {
    const wrap = this.querySelector('[data-review-stars]');
    if (wrap) wrap.classList.remove('is-pending');
  }

  // The star row sits above the quote and belongs to whichever review is showing,
  // so it repaints on every slide change. Fallback slides carry no rating and keep
  // the server-rendered row.
  paintActiveStars() {
    const wrap = this.querySelector('[data-review-stars]');
    if (!wrap) return;
    const active = this.slides[this.index];
    const rating = active && Number(active.dataset.rating);
    if (rating) this.paintStars(wrap, rating);
  }

  async renderCount() {
    const target = this.querySelector('[data-review-count]');
    if (!target) return;

    // The markup ships with `is-pending` so the fallback never flashes. Every
    // exit path below must reveal it again.
    const reveal = () => target.classList.remove('is-pending');

    const { store, reviewType, skus } = this.dataset;
    if (!store) {
      reveal();
      return;
    }

    const params = {
      store,
      type: reviewType || 'product_review',
      sort: 'date_desc',
      page: '1',
      per_page: '1',
      lang: 'en',
    };
    if (skus) params.sku = skus;

    try {
      const data = await fetch('https://api.reviews.io/timeline/data?' + new URLSearchParams(params)).then((r) =>
        r.ok ? r.json() : null,
      );
      const total = data && data.stats && data.stats.review_count;
      if (total) {
        const suffix = target.dataset.reviewCountSuffix || '';
        target.textContent = [Number(total).toLocaleString(), suffix].filter(Boolean).join(' ');
      }
    } catch (e) {
      // Keep the server-rendered fallback.
    } finally {
      reveal();
    }
  }

  // A reviewer leaves whole stars, so `_source.rating` is an integer 1-5 and there is
  // no half state here. Product cards average many reviews, so those do need halves.
  paintStars(wrap, rating) {
    const slots = Array.from(wrap.querySelectorAll('.testimonial-carousel__star'));
    const filled = Math.round(rating);
    if (!slots.length || !Number.isFinite(filled) || filled <= 0) return;

    slots.forEach((slot, i) => {
      const state = i < filled ? 'full' : 'empty';
      slot.className = 'testimonial-carousel__star testimonial-carousel__star--' + state;
    });

    wrap.setAttribute('aria-label', 'Rated ' + filled + ' out of ' + slots.length + ' stars');
  }

  render(reviews) {
    this.slidesWrap.textContent = '';
    if (this.dotsWrap) this.dotsWrap.textContent = '';

    reviews.forEach((review, i) => {
      const figure = document.createElement('figure');
      figure.className = 'testimonial-carousel__slide' + (i === 0 ? ' is-active' : '');
      figure.setAttribute('role', 'listitem');
      figure.dataset.index = i;
      if (review.rating) figure.dataset.rating = review.rating;
      figure.toggleAttribute('hidden', i !== 0);

      const quote = document.createElement('blockquote');
      quote.className = 'testimonial-carousel__quote';
      quote.textContent = '“' + review.comments.trim() + '”';
      figure.appendChild(quote);

      const caption = document.createElement('figcaption');
      caption.className = 'testimonial-carousel__author';

      if (review.author) {
        const name = document.createElement('span');
        name.className = 'testimonial-carousel__author-name';
        name.textContent = review.author;
        caption.appendChild(name);
      }

      if (review.product_name) {
        const sep = document.createElement('span');
        sep.className = 'testimonial-carousel__sep';
        sep.setAttribute('aria-hidden', 'true');
        sep.textContent = '|';
        caption.appendChild(sep);

        const source = document.createElement(review.product_link ? 'a' : 'span');
        source.className = 'testimonial-carousel__source';
        if (review.product_link) source.href = review.product_link;
        source.textContent = review.product_name;
        caption.appendChild(source);
      }

      figure.appendChild(caption);
      this.slidesWrap.appendChild(figure);

      if (this.dotsWrap) {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'testimonial-carousel__dot' + (i === 0 ? ' is-active' : '');
        dot.dataset.index = i;
        dot.setAttribute('role', 'tab');
        dot.setAttribute('aria-selected', i === 0 ? 'true' : 'false');
        dot.setAttribute('aria-label', 'Review ' + (i + 1));
        this.dotsWrap.appendChild(dot);
      }
    });
  }

  go(target) {
    if (!this.slides.length) return;
    const n = this.slides.length;
    this.index = (target + n) % n;

    this.slides.forEach((slide, i) => {
      const active = i === this.index;
      slide.classList.toggle('is-active', active);
      slide.toggleAttribute('hidden', !active);
    });
    this.dots.forEach((dot, i) => {
      const active = i === this.index;
      dot.classList.toggle('is-active', active);
      dot.setAttribute('aria-selected', active ? 'true' : 'false');
    });

    this.paintActiveStars();
  }
}

customElements.define('testimonial-carousel-live', TestimonialCarouselLive);
